import { Box, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import enceinte from "../assets/newImg/femme enceinte.png";
import SEO from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW } from "../tokens";

const TRIMESTRES = [
  {
    num: "T1",
    title: "Premier trimestre",
    period: "1–12 sem.",
    items: [
      "Nausées, troubles digestifs",
      "Tensions cervicales",
      "Fatigue & stress",
      "Douleurs pelviennes précoces",
    ],
  },
  {
    num: "T2",
    title: "Deuxième trimestre",
    period: "13–26 sem.",
    items: [
      "Lombalgies & sciatiques",
      "Douleurs ligamentaires",
      "Reflux gastrique",
      "Préparation du bassin",
    ],
  },
  {
    num: "T3",
    title: "Troisième trimestre",
    period: "27–40 sem.",
    items: [
      "Préparation à l'accouchement",
      "Mobilité du bassin & sacrum",
      "Douleurs périnéales (SPB)",
      "Détente du diaphragme",
    ],
  },
];

const PENDANT = [
  {
    title: "Lombalgie & sciatique",
    desc: "Soulagement des douleurs lombaires liées à la modification hormonale et à la prise de poids.",
  },
  {
    title: "Douleurs pelviennes",
    desc: "Symptôme fréquent, sans choc, traité en douceur.",
  },
  {
    title: "Reflux gastrique",
    desc: "Préparation du diaphragme et travail viscéral pour améliorer le confort digestif.",
  },
  {
    title: "Préparation accouchement",
    desc: "Mobilité du bassin et du sacrum pour faciliter le travail.",
  },
];

const APRES = [
  {
    title: "Rééquilibrage du bassin",
    desc: "Suite à l'expulsion ou à une césarienne.",
  },
  {
    title: "Douleurs cicatricielles",
    desc: "Travail des cicatrices d'épisiotomie ou de césarienne.",
  },
  {
    title: "Troubles allaitement",
    desc: "Contractures, douleurs dorsales liées à la posture d'allaitement.",
  },
  {
    title: "Retour de couches",
    desc: "Transit, prolapsus, énergies générales.",
  },
];

const FemmeEnceinte = () => (
  <>
    <SEO
      title="Ostéopathie femme enceinte & post-partum  Balma"
      description="Ostéopathie pendant la grossesse et après l'accouchement. Lombalgies, sciatiques, préparation à l'accouchement, rééquilibrage post-partum. Cabinet à Balma (31130)."
      path="/pathologies/femme-enceinte"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: "https://www.balma-osteopathe.fr/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: "https://www.balma-osteopathe.fr/pathologies",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Femme enceinte",
            item: "https://www.balma-osteopathe.fr/pathologies/femme-enceinte",
          },
        ],
      }}
    />
    <Box component="section" sx={SX.pageHeroSection}>
      <Box
        sx={{
          ...SX.pageHeroInner,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Femme enceinte &amp; post-partum
          </Box>
          <Typography variant="h1" component="h1" sx={SX.displayTitle}>
            Vous accompagner, <em>à chaque trimestre.</em>
          </Typography>
          <Typography sx={SX.pageHeroSub}>
            Pendant la grossesse et après l'accouchement, le corps traverse des
            transformations profondes. Mon rôle : prévenir et soulager les
            douleurs, préparer l'accouchement, accompagner le retour à
            l'équilibre.
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "220px", md: "360px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            component="img"
            src={enceinte}
            alt="Ostéopathie pour femmes enceintes"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Trimestres ──────────────────────────── */}
    <Box component="section" sx={{ py: { xs: "2.5rem", md: "4rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Bénéfices par trimestre
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "1.25rem" }}
        >
          Trois trimestres, <em>trois approches.</em>
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: ".75rem",
            background: "#f5f0ea",
            border: `1px solid ${C.border}`,
            borderRadius: R.base,
            px: "1.25rem",
            py: "1rem",
            mb: "2rem",
            fontSize: ".82rem",
            color: C.textMuted,
            lineHeight: 1.65,
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: "1rem", mt: ".05rem", flexShrink: 0 }}
          >
            ℹ️
          </Box>
          <Box>
            Les consultations ostéopathiques sont recommandées{" "}
            <strong>à partir du 2ème trimestre</strong>. Le tableau ci-dessous
            présente les signes qui peuvent survenir à chaque trimestre. En cas
            de doute avant 13 semaines, consultez votre médecin ou sage-femme.
          </Box>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: "1.25rem",
          }}
        >
          {TRIMESTRES.map((t) => (
            <Box
              key={t.num}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.75rem",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: C.primary,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: F.display,
                  fontSize: "1rem",
                  fontWeight: 400,
                  mb: "1rem",
                }}
              >
                {t.num}
              </Box>
              <Typography
                sx={{
                  fontSize: ".88rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".2rem",
                }}
              >
                {t.title}
              </Typography>
              <Typography
                sx={{ fontSize: ".75rem", color: C.accent, mb: "1rem" }}
              >
                {t.period}
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: ".35rem" }}
              >
                {t.items.map((item) => (
                  <Typography
                    key={item}
                    sx={{
                      fontSize: ".8rem",
                      color: C.textMuted,
                      pl: ".75rem",
                      borderLeft: `2px solid ${C.border}`,
                      lineHeight: 1.4,
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Forfait maman + bébé ─────────────────── */}
    <Box component="section" sx={{ background: C.primary, py: "5rem" }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: ".65rem",
              fontWeight: 600,
              letterSpacing: "2.5px",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.5)",
              mb: ".75rem",
            }}
          >
            Forfait bébé
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(1.9rem, 3.5vw, 2.8rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.65)" },
            }}
          >
            Maman + bébé, <em>un seul rendez-vous.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".85rem",
              color: "rgba(255,255,255,.6)",
              maxWidth: "400px",
              lineHeight: 1.75,
              mb: "1.5rem",
            }}
          >
            Au dernier trimestre, je propose un forfait pour examiner la maman
            et l'ostéopathe pour le bébé. Une seule séance, deux soins.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: ".4rem",
              mb: "1.75rem",
            }}
          >
            {[
              "Consultation maman complète (45 min)",
              "Bilan ostéopathique nourrisson (20 min)",
              "Conseils allaitement & posture",
              "Compte-rendu détaillé",
            ].map((item) => (
              <Typography
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".6rem",
                  fontSize: ".82rem",
                  color: "rgba(255,255,255,.72)",
                  "&::before": {
                    content: '"✓"',
                    fontWeight: 600,
                    color: "rgba(255,255,255,.5)",
                    flexShrink: 0,
                  },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
          <Box
            component="a"
            href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
            target="_blank"
            rel="noreferrer"
            sx={SX.btnOutlineWhite}
          >
            Réserver le forfait
          </Box>
        </Box>

        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "5rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            95
            <Box
              component="sup"
              sx={{ fontSize: "2rem", fontWeight: 400, verticalAlign: "super" }}
            >
              €
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: ".72rem",
              color: "rgba(255,255,255,.4)",
              mt: ".6rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            au lieu de 110 €<br />
            les deux séances séparées
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Pendant / Après ─────────────────────── */}
    <Box component="section" sx={{ py: "5.5rem", background: C.bgAlt }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: "3rem", md: "4rem" },
        }}
      >
        {[
          { title: "Pendant la grossesse", items: PENDANT },
          { title: "Après l'accouchement", items: APRES },
        ].map(({ title, items }) => (
          <Box key={title}>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: C.accent,
                mb: "1.5rem",
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
            >
              {items.map((p) => (
                <Box key={p.title}>
                  <Typography
                    sx={{
                      fontSize: ".88rem",
                      fontWeight: 600,
                      color: C.text,
                      mb: ".25rem",
                    }}
                  >
                    {p.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: ".8rem",
                      color: C.textMuted,
                      lineHeight: 1.65,
                    }}
                  >
                    {p.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>

    {/* ── Contre-indications ──────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "4rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Précautions
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "1.5rem" }}
        >
          Quand demander un avis <em>médical d'abord ?</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "1rem",
          }}
        >
          {[
            {
              title: "Hypertension artérielle",
              desc: "Une tension artérielle élevée non contrôlée nécessite un avis médical avant toute séance.",
            },
            {
              title: "Troubles du développement fœtal",
              desc: "Toute anomalie suivie médicalement (placenta prævia, RCIU…) doit être stabilisée avant consultation.",
            },
            {
              title: "Menace d'accouchement prématuré",
              desc: "En cas de contractions précoces ou de col ouvert, l'ostéopathie est contre-indiquée jusqu'à stabilisation.",
            },
            {
              title: "Grossesse à haut risque",
              desc: "Certaines grossesses gémellaires ou pathologiques nécessitent une coordination avec l'équipe médicale.",
            },
          ].map((item) => (
            <Box
              key={item.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.base,
                px: "1.25rem",
                py: "1rem",
                display: "flex",
                gap: ".75rem",
                alignItems: "flex-start",
              }}
            >
              <Box
                component="span"
                sx={{
                  color: C.accent,
                  fontWeight: 700,
                  fontSize: ".9rem",
                  flexShrink: 0,
                  mt: ".05rem",
                }}
              >
                !
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".85rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".2rem",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".78rem",
                    color: C.textMuted,
                    lineHeight: 1.6,
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── CTA liens internes ─────────────────── */}
    <Box
      sx={{
        maxWidth: maxW,
        mx: "auto",
        px: { xs: "1rem", md: "2.5rem" },
        py: { xs: "3rem", md: "4rem" },
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <Box
        component={Link}
        to="/tarifs-et-remboursements"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Consulter les tarifs →
      </Box>
      <Box
        component={Link}
        to="/faq"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Questions fréquentes →
      </Box>
    </Box>
  </>
);

export default FemmeEnceinte;
